'use client';

import { useContext } from 'react';
import { RingSelectionContext } from 'components/ring-air/RingSelectionContext';
import { aeEmirateGroupOf } from './emirates';
import { useAeDeliveryWindow } from './useAeDeliveryWindow';

/**
 * Single composition point for the AE delivery promise: reads the selected
 * emirate from RingSelectionContext, derives the emirate group, and resolves
 * the live GST window for it. All AE ETA surfaces (card, size accordion,
 * order summary, both bottom bars) consume this so they can never disagree
 * on group logic; fallback surfaces take shippingEstimate from the context
 * themselves.
 *
 * @param enabled - the caller's AE visibility gate (region === 'AE' &&
 * AE_DELIVERY_ETA_ENABLED); gates the window timer, see useAeDeliveryWindow.
 * @param isSizingKit - false while a numeric ring size is selected: the
 * express promise is sizing-kit only, so the emirate is treated as null and
 * group/window resolve from the null default — emirate, group, and window
 * can then never disagree (a stored emirate restored by a ?size=N deep link
 * must not leak its group's window into the inert default line). Callers
 * that gate themselves on the sizing kit (bars, order summary) omit it.
 */
export const useAeDeliveryEta = (enabled: boolean, isSizingKit = true) => {
  const { selectedEmirate, setSelectedEmirate } =
    useContext(RingSelectionContext);
  const effectiveEmirate = isSizingKit ? selectedEmirate : null;
  const group = aeEmirateGroupOf(effectiveEmirate);
  const windowState = useAeDeliveryWindow(enabled, group);

  return {
    selectedEmirate: effectiveEmirate,
    setSelectedEmirate,
    group,
    windowState,
    hasError: windowState === 'error',
  };
};
